// file: client/src/offline/outbox.js
/**
 * MODULE 09 — the durable mutation queue (the driver-app discipline,
 * generalized). FIFO, ULID-keyed, HMAC-chained at append time; statuses
 * pending → (sent) → done | failed (dead-letter, surfaced — never an
 * infinite retry: the AUD-010 lesson is structural here).
 */
import { ulid } from "./ulid.js";
import { signOp } from "./chain.js";
import { idbAll, idbPut, idbDelete, metaGet, metaSet } from "./idb.js";
import { useOfflineStore, loadDevice } from "./offlineStore.js";

const MAX_ATTEMPTS = 6;

export async function refreshCounts() {
  const all = await idbAll("outbox");
  useOfflineStore.getState().setCounts({
    pending: all.filter((o) => o.statut === "pending").length,
    deadLetters: all.filter((o) => o.statut === "failed").length,
  });
}

/** Next provisional sequence for HL-{device}-{seq} (plan §6.4). */
async function nextProvisionalSeq() {
  const n = ((await metaGet("hl_seq")) ?? 0) + 1;
  await metaSet("hl_seq", n);
  return n;
}

/**
 * Append one op. Returns { op, numero_provisoire } — the caller builds its
 * synthetic UI response from these.
 */
export async function enqueue(op_type, payload, { user_id } = {}) {
  const device = loadDevice();
  if (!device?.secret) throw new Error("OFFLINE_DEVICE_NOT_ENROLLED");
  const prev = (await metaGet("chain_head")) ?? "GENESIS";
  const seq = await nextProvisionalSeq();
  const numero_provisoire = `HL-${device.label_code ?? device.device_id.slice(0, 4).toUpperCase()}-${String(seq).padStart(4, "0")}`;
  const op = {
    id: ulid(),
    op_type,
    offline_ts: new Date().toISOString(),
    user_id: user_id ?? null,
    numero_provisoire,
    payload,
    prev,
  };
  op.hmac = await signOp(device.secret, op, prev);
  await metaSet("chain_head", op.hmac);
  await idbPut("outbox", {
    ...op,
    statut: "pending",
    attempts: 0,
    created_at: op.offline_ts,
    last_error: null,
  });
  await refreshCounts();
  return { op, numero_provisoire };
}

/** Ordered pending ops (ULID order = append order). */
export async function pendingOps() {
  const all = await idbAll("outbox");
  return all
    .filter((o) => o.statut === "pending")
    .sort((a, b) => (a.id < b.id ? -1 : 1));
}

export async function allOps() {
  const all = await idbAll("outbox");
  return all.sort((a, b) => (a.id < b.id ? -1 : 1));
}

/** Apply one batch result coming back from POST /sync/batch. */
export async function applyResult(opId, result) {
  const all = await idbAll("outbox");
  const row = all.find((o) => o.id === opId);
  if (!row) return;
  if (["APPLIED", "DUPLICATE", "CONFLICT"].includes(result.statut)) {
    // Done from the terminal's perspective; conflicts live server-side in
    // the review queue. Keep a small trace then drop the row.
    await idbDelete("outbox", opId);
  } else {
    const attempts = (row.attempts ?? 0) + 1;
    const failed =
      result.statut === "REJECTED" || attempts >= MAX_ATTEMPTS;
    await idbPut("outbox", {
      ...row,
      attempts,
      statut: failed ? "failed" : "pending",
      last_error: result.raison ?? result.statut,
    });
  }
}

export async function markNetworkFailure(opIds) {
  // Network failure ≠ op failure: ops stay pending (retry with backoff);
  // only attempts on REJECTED count toward the dead-letter cap.
  void opIds;
}

export async function discardDeadLetter(opId) {
  await idbDelete("outbox", opId);
  await refreshCounts();
}

export async function retryDeadLetter(opId) {
  const all = await idbAll("outbox");
  const row = all.find((o) => o.id === opId);
  if (row) {
    await idbPut("outbox", { ...row, statut: "pending", attempts: 0 });
    await refreshCounts();
  }
}
